<script setup>
// Estúdio Social — Métricas (Task 13, última tela do módulo). Consome
// GET .../social/posts/metrics: { posts: [{id, content(<=60), published_at,
// platforms, metrics}], totals: {published_count, reach?, impressions?,
// likes?...}, accounts: [{platform, username, followers(null por ora)}] }.
//
// Contrato real (posts_controller.rb#aggregate_totals): published_count
// SEMPRE existe em `totals` (é `posts.size`, pode ser 0); os demais campos
// (reach/impressions/likes/comments/shares/clicks) só aparecem quando a
// SOMA é positiva — ausência é sinal de "sem esse dado", nunca "zero". Por
// isso os cards de total nunca inventam um "0" pra campo ausente: filtram
// pela CHAVE existir em `totals`, não pelo valor.
//
// Sem posts publicados nos últimos 30 dias (totals.published_count === 0):
// em vez de renderizar cards/tabela vazios, mostra um painel "vazio digno"
// explicando que métricas aparecem depois de publicar, com CTA pro
// calendário — mesmo vocabulário de estado vazio já usado nas Tasks 10/12.
// A lista de contas conectadas (informação independente de ter post ou
// não) continua visível nesse caso, se houver alguma conta conectada.
//
// Sem NENHUM post com métrica numérica ainda (published existe, mas
// `metrics` de todos os posts está vazio — caso esperado até a primeira
// sincronização diária do analytics): a tabela ainda lista os posts
// (conteúdo/data/redes), só a coluna de métricas vira uma única coluna
// "—" com dica explicando o motivo, em vez de sumir a tabela inteira.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { format } from 'date-fns';
import { useAccount } from 'dashboard/composables/useAccount';
import { useDateFnsLocale } from 'dashboard/composables/useDateFnsLocale';
import socialAPI from 'dashboard/api/social';
import Button from 'dashboard/components-next/button/Button.vue';
import SocialNav from './SocialNav.vue';
import { SOCIAL_PLATFORMS } from './constants';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { dateFnsLocale } = useDateFnsLocale();

const isLoading = ref(true);
const loadError = ref(null); // { status, message } | null
const posts = ref([]);
const totals = ref({ published_count: 0 });
const connectedAccounts = ref([]);

// Ordem fixa pros campos conhecidos (cards e colunas da tabela seguem a
// mesma ordem); campos numéricos desconhecidos que o analytics vier a
// mandar no futuro entram no fim, em ordem alfabética, em vez de somem.
const KNOWN_METRIC_FIELDS = [
  'reach',
  'impressions',
  'likes',
  'comments',
  'shares',
  'clicks',
];

const TOTAL_CARD_META = {
  published_count: { icon: 'i-lucide-send', color: 'text-n-brand' },
  reach: { icon: 'i-lucide-eye', color: 'text-n-blue-10' },
  impressions: { icon: 'i-lucide-bar-chart-2', color: 'text-n-iris-10' },
  likes: { icon: 'i-lucide-heart', color: 'text-n-ruby-10' },
  comments: { icon: 'i-lucide-message-circle', color: 'text-n-amber-10' },
  shares: { icon: 'i-lucide-share-2', color: 'text-n-teal-10' },
  clicks: { icon: 'i-lucide-mouse-pointer-click', color: 'text-n-slate-11' },
};

// Label de uma métrica (card de total ou coluna da tabela). `metricColumns`
// pode trazer um campo que o analytics inventou e que `social.json` ainda
// não conhece (ex.: `ctr`) — sem esse fallback, `t()` devolve a própria
// CHAVE quando não encontra tradução, e a UI mostraria literalmente
// "SOCIAL_STUDIO.METRICS.TOTALS.CTR" pro usuário. Humaniza o nome cru
// (underscore vira espaço) nesse caso em vez de esconder a coluna.
const metricLabel = col => {
  const key = `SOCIAL_STUDIO.METRICS.TOTALS.${col.toUpperCase()}`;
  const label = t(key);
  return label === key ? col.replace(/_/g, ' ') : label;
};

const fetchMetrics = async () => {
  isLoading.value = true;
  loadError.value = null;
  try {
    const { data } = await socialAPI.metrics();
    posts.value = data.posts || [];
    totals.value = data.totals || { published_count: 0 };
    connectedAccounts.value = data.accounts || [];
  } catch (error) {
    loadError.value = {
      status: error.response?.status,
      message:
        error.response?.data?.error ||
        error.response?.data?.errors?.[0] ||
        t('SOCIAL_STUDIO.METRICS.LOADING_ERROR'),
    };
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchMetrics);

const hasPublishedPosts = computed(
  () => (totals.value.published_count || 0) > 0
);

// Cards de total: published_count sempre que houver posts (branch acima já
// garante isso); os demais só quando a CHAVE existe em totals (backend só
// inclui quando a soma é positiva — nunca um "0" fabricado aqui).
const totalCards = computed(() =>
  ['published_count', ...KNOWN_METRIC_FIELDS]
    .filter(
      key => totals.value[key] !== undefined && totals.value[key] !== null
    )
    .map(key => ({
      key,
      value: totals.value[key],
      icon: TOTAL_CARD_META[key].icon,
      color: TOTAL_CARD_META[key].color,
      label: metricLabel(key),
    }))
);

// União das chaves NUMÉRICAS de metrics entre os posts retornados — colunas
// dinâmicas da tabela. Campos conhecidos primeiro (ordem estável), depois
// qualquer campo novo do analytics em ordem alfabética.
const metricColumns = computed(() => {
  const found = new Set();
  posts.value.forEach(post => {
    Object.entries(post.metrics || {}).forEach(([key, value]) => {
      if (typeof value === 'number') found.add(key);
    });
  });
  const known = KNOWN_METRIC_FIELDS.filter(key => found.has(key));
  const unknown = [...found]
    .filter(key => !KNOWN_METRIC_FIELDS.includes(key))
    .sort();
  return [...known, ...unknown];
});

const platformMeta = key => SOCIAL_PLATFORMS.find(p => p.key === key);
const platformLabel = key =>
  t(`SOCIAL_STUDIO.ACCOUNTS.PLATFORMS.${key.toUpperCase()}`);

const formatPostDate = raw => {
  if (!raw) return '—';
  return format(new Date(raw), 'PPp', { locale: dateFnsLocale.value });
};

const goToCalendar = () =>
  router.push(accountScopedRoute('social_calendar_index'));
</script>

<template>
  <div class="w-full h-full overflow-y-auto bg-n-background">
    <div class="max-w-7xl mx-auto p-6 space-y-6">
      <SocialNav />

      <!-- Cabeçalho: texto estático, não depende de dado carregado (lição da
         Task 10 — o título "conecte sua primeira rede" piscava porque não
         era gated por isLoading). -->
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('SOCIAL_STUDIO.METRICS.TITLE') }}
        </h1>
        <p class="mt-1 text-sm text-n-slate-11 max-w-md">
          {{ t('SOCIAL_STUDIO.METRICS.SUBTITLE') }}
        </p>
      </div>

      <!-- Erro ao carregar (inclui 402: módulo não ativo) -->
      <div
        v-if="!isLoading && loadError"
        class="rounded-2xl border border-n-weak bg-n-solid-2 p-6 text-center space-y-3"
      >
        <span
          class="i-lucide-cloud-alert mx-auto block size-8 text-n-slate-10"
        />
        <p class="text-sm text-n-slate-11">{{ loadError.message }}</p>
        <Button
          v-if="loadError.status === 402"
          :label="t('SOCIAL_STUDIO.METRICS.UPGRADE_CTA')"
          size="sm"
          @click="router.push(accountScopedRoute('billing_settings_index'))"
        />
        <Button
          v-else
          :label="t('SOCIAL_STUDIO.METRICS.RETRY')"
          variant="outline"
          size="sm"
          @click="fetchMetrics"
        />
      </div>

      <!-- Loading: esqueleto no formato do conteúdo final (cards + tabela),
         mesmo padrão de Task 10/12. -->
      <div v-else-if="isLoading" class="space-y-6">
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div
            v-for="n in 4"
            :key="n"
            class="h-20 animate-pulse rounded-2xl border border-n-weak bg-n-slate-3"
          />
        </div>
        <div
          class="h-40 animate-pulse rounded-2xl border border-n-weak bg-n-slate-3"
        />
      </div>

      <template v-else>
        <!-- Contas conectadas: informação independente de ter post publicado
           ou não — reaparece mesmo no estado vazio pra reassegurar que a
           conexão funcionou. -->
        <section v-if="connectedAccounts.length" class="space-y-3">
          <h2
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ t('SOCIAL_STUDIO.METRICS.ACCOUNTS_TITLE') }}
          </h2>
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <div
              v-for="account in connectedAccounts"
              :key="`${account.platform}-${account.username}`"
              class="flex items-center gap-3 rounded-2xl border border-n-weak bg-n-solid-2 p-3"
            >
              <span
                class="flex size-9 shrink-0 items-center justify-center rounded-xl"
                :class="[
                  platformMeta(account.platform)?.badgeClass,
                  platformMeta(account.platform)?.iconTone || 'text-white',
                ]"
              >
                <span
                  :class="platformMeta(account.platform)?.icon"
                  class="size-4"
                />
              </span>
              <div class="min-w-0 flex-1">
                <p class="truncate text-sm font-medium text-n-slate-12">
                  {{ account.username || platformLabel(account.platform) }}
                </p>
                <p class="text-xs text-n-slate-10">
                  {{ platformLabel(account.platform) }}
                </p>
              </div>
              <div class="shrink-0 text-right">
                <p class="text-sm font-semibold tabular-nums text-n-slate-12">
                  {{ account.followers ?? '—' }}
                </p>
                <p class="text-[11px] text-n-slate-10">
                  {{ t('SOCIAL_STUDIO.METRICS.FOLLOWERS_LABEL') }}
                </p>
              </div>
            </div>
          </div>
        </section>

        <!-- Vazio digno: nenhum post publicado nos últimos 30 dias. -->
        <div
          v-if="!hasPublishedPosts"
          class="rounded-2xl border border-n-weak bg-n-solid-2 p-8 text-center space-y-3"
        >
          <span
            class="i-lucide-bar-chart-3 mx-auto block size-8 text-n-slate-10"
          />
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('SOCIAL_STUDIO.METRICS.EMPTY.TITLE') }}
          </h3>
          <p class="mx-auto max-w-sm text-sm text-n-slate-11">
            {{ t('SOCIAL_STUDIO.METRICS.EMPTY.SUBTITLE') }}
          </p>
          <Button
            :label="t('SOCIAL_STUDIO.METRICS.EMPTY.CTA')"
            size="sm"
            @click="goToCalendar"
          />
        </div>

        <template v-else>
          <!-- Cards de total -->
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div
              v-for="card in totalCards"
              :key="card.key"
              class="rounded-2xl border border-n-weak bg-n-solid-2 p-4"
            >
              <div class="mb-2 flex items-center gap-2">
                <span :class="[card.icon, card.color]" class="size-4" />
                <span class="text-xs text-n-slate-11">{{ card.label }}</span>
              </div>
              <p class="text-2xl font-semibold tabular-nums text-n-slate-12">
                {{ card.value }}
              </p>
            </div>
          </div>

          <!-- Tabela por post -->
          <section class="space-y-3">
            <h2
              class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
            >
              {{ t('SOCIAL_STUDIO.METRICS.TABLE.TITLE') }}
            </h2>
            <div class="overflow-x-auto rounded-xl border border-n-weak">
              <table class="w-full text-sm">
                <thead class="bg-n-alpha-2 text-xs text-n-slate-11">
                  <tr>
                    <th class="px-4 py-2.5 text-left font-medium">
                      {{ t('SOCIAL_STUDIO.METRICS.TABLE.CONTENT') }}
                    </th>
                    <th class="px-4 py-2.5 text-left font-medium">
                      {{ t('SOCIAL_STUDIO.METRICS.TABLE.DATE') }}
                    </th>
                    <th class="px-4 py-2.5 text-left font-medium">
                      {{ t('SOCIAL_STUDIO.METRICS.TABLE.PLATFORMS') }}
                    </th>
                    <template v-if="metricColumns.length">
                      <th
                        v-for="col in metricColumns"
                        :key="col"
                        class="px-4 py-2.5 text-right font-medium"
                      >
                        {{ metricLabel(col) }}
                      </th>
                    </template>
                    <th v-else class="px-4 py-2.5 text-right font-medium">
                      {{ t('SOCIAL_STUDIO.METRICS.TABLE.NO_METRICS_COLUMN') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="post in posts"
                    :key="post.id"
                    class="border-t border-n-weak hover:bg-n-alpha-1"
                  >
                    <td class="max-w-xs truncate px-4 py-3 text-n-slate-12">
                      {{ post.content || '—' }}
                    </td>
                    <td class="whitespace-nowrap px-4 py-3 text-n-slate-11">
                      {{ formatPostDate(post.published_at) }}
                    </td>
                    <td class="px-4 py-3">
                      <div class="flex items-center gap-1">
                        <span
                          v-for="platform in post.platforms"
                          :key="platform.social_account_id"
                          :title="platformLabel(platform.platform)"
                          class="flex size-6 shrink-0 items-center justify-center rounded-md"
                          :class="[
                            platformMeta(platform.platform)?.badgeClass,
                            platformMeta(platform.platform)?.iconTone ||
                              'text-white',
                          ]"
                        >
                          <span
                            :class="platformMeta(platform.platform)?.icon"
                            class="size-3"
                          />
                        </span>
                      </div>
                    </td>
                    <template v-if="metricColumns.length">
                      <td
                        v-for="col in metricColumns"
                        :key="col"
                        class="px-4 py-3 text-right tabular-nums text-n-slate-12"
                      >
                        {{
                          typeof post.metrics?.[col] === 'number'
                            ? post.metrics[col]
                            : '—'
                        }}
                      </td>
                    </template>
                    <td v-else class="px-4 py-3 text-right text-n-slate-10">
                      —
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-if="!metricColumns.length" class="text-xs text-n-slate-10">
              {{ t('SOCIAL_STUDIO.METRICS.TABLE.NO_METRICS_HINT') }}
            </p>
          </section>
        </template>
      </template>
    </div>
  </div>
</template>
